import { useEffect, useState } from 'react';

import { AuthenticationStore, useAuthentication } from '@freelancelabs/components';

interface Gate {
    /** The portal must not open: the requirement stands and no factor is registered. */
    blocked: boolean;
    /** The pool has not answered yet. Nothing is decided, so nothing is rendered. */
    checking: boolean;
}

/**
 * Whether the portal must stop somebody before letting them in, because one of their
 * accounts requires multi-factor authentication and they carry no factor (PCG-303).
 *
 * **TWO FACTS, TWO SOURCES, and neither is deducible from the other.** The requirement
 * is a policy of the account and comes from our API, on `viewer.mfaRequired`. Whether a
 * factor is registered is a fact of the Cognito pool, read through the authentication
 * store. `user.mfa_method` in our database is a display mirror the schema forbids using
 * to authorise, so it is not an answer to the second question.
 *
 * **It closes the door the account application cannot close.** The session cookie sits
 * on `.freelance.com`, so somebody already signed in reaches this host directly without
 * ever passing through `account` again: a gate there catches the first entry and
 * nothing else.
 *
 * **It fails OPEN, the same way and for the same reason as `account`.** A failed read of
 * the pool lets the person in and the demand is deferred to the next load. Failing
 * closed would lock somebody out of the portal on a transient error, on a requirement
 * that is an onboarding control rather than a boundary as long as no factor exists.
 * `required` being false, which is what a failed `viewer` query also produces, has the
 * same effect by construction.
 *
 * **`checking` is not `blocked`.** Rendering the barrier while the pool is being asked
 * would flash it in front of everybody who already carries a factor, which is most
 * people once a client has been on the requirement for a week.
 */
export const useMfaGate = (required: boolean | null | undefined): Gate => {
    const { fetchMfaSettings, isFederatedSession } = useAuthentication() as AuthenticationStore;
    const [state, setState] = useState<Gate>({ blocked: false, checking: false });

    useEffect(() => {
        if (required !== true) {
            // Nothing to ask the pool: no requirement, or the query has not answered.
            setState({ blocked: false, checking: false });
            return;
        }

        let live = true;
        setState({ blocked: false, checking: true });

        /**
         * PAS DE MFA SI C'EST DU SSO (PCG-304), décidé par Mohamed le 2026-08-28, et **cette
         * porte-ci s'amende avec celle d'`account`, jamais seule** : le cookie de session est
         * posé sur `.freelance.com`, donc dispenser d'un côté et pas de l'autre laisserait le
         * mur se dresser ici juste après qu'`account` a laissé passer.
         *
         * **Attention, c'est un fait de la SESSION et non de la personne** : la même personne
         * revenue par mot de passe retombe sous la barrière, ce qui est voulu. Ce qui la
         * dispense est le second facteur qu'Entra ID a déjà demandé sur CE chemin-là.
         *
         * La lecture échoue vers `false`, donc vers la barrière : la règle du dépôt est que
         * ces deux portes échouent ouvertes sur le POOL, pas qu'une lecture ratée invente une
         * dispense.
         */
        isFederatedSession()
            .then(async (federated) => {
                /*
                 * On ne demande RIEN au pool dans ce cas, et surtout on ne fabrique pas une
                 * réponse à sa place : renvoyer un `totp: true` de complaisance ferait dire
                 * au pool qu'un facteur existe, ce qu'il n'a pas dit. Deux faits, deux
                 * sources, c'est la règle de ce fichier depuis PCG-303.
                 */
                if (federated) {
                    return false;
                }
                const { totp } = await fetchMfaSettings();
                return !totp;
            })
            .then((blocked) => {
                if (live) {
                    setState({ blocked, checking: false });
                }
            })
            .catch(() => {
                if (live) {
                    setState({ blocked: false, checking: false });
                }
            });

        // The person may sign out, or the query may answer twice: a late resolution
        // must not put the barrier back up on a screen that has moved on.
        return () => {
            live = false;
        };
    }, [required]);

    return state;
};

export default useMfaGate;
